import { StageIcon, type StageState } from 'components/StageIcon'
import { cn } from 'lib/utils'
import { motion } from 'framer-motion'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  SHARED_LAYOUT_COLOR,
  SHARED_LAYOUT_LOGO,
  SHARED_LAYOUT_NAME,
  SHARED_LAYOUT_TONE,
} from './shared-layout'
import type { LogoBackdrop } from '@mondaygpt/types'
import { ScanStatus } from './use-scan-stream'
import { Polaroid, PolaroidSection } from './Polaroid'
import { InstagramPolaroid } from './InstagramPolaroid'

/** Animation for each DiscoveryFeed reveal — hoisted so framer doesn't see a fresh object identity per render. */
const REVEAL_ENTER = {
  initial: { opacity: 0, x: 8 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.4, ease: [0.2, 0.8, 0.2, 1] as const },
}

/**
 * Logo image with a graceful broken-state. If `src` fails to load (404, ad
 * blocker, network), render nothing instead of the browser's broken-image
 * icon — BrandCardStep's LogoSlot has the matching onError fallback to the
 * businessName initial, so the morph either lands a real logo or quietly
 * dissolves into the initial. Either way, never the broken-icon glyph.
 */
const DiscoveryLogoImg: React.FC<{ src: string; backdrop?: LogoBackdrop }> = ({
  src,
  backdrop,
}) => {
  const [broken, setBroken] = useState(false)
  if (broken) return null
  return (
    <motion.img
      {...REVEAL_ENTER}
      layoutId={SHARED_LAYOUT_LOGO}
      src={src}
      alt=""
      className={cn(
        'h-7 w-7 shrink-0 rounded-[7px] border border-line-bright object-contain p-1',
        backdrop === 'dark' ? 'bg-bg-3' : 'bg-white'
      )}
      loading="eager"
      onError={() => setBroken(true)}
    />
  )
}

/**
 * Per-source brand-learning preview phase, lifted from the parent's
 * `useBrandLearningPreviewBatch`. `null` means the source wasn't attempted in
 * this batch (the user didn't provide that input).
 */
export interface BrandLearningProgress {
  instagram: 'pending' | 'done' | 'failed' | null
  website: 'pending' | 'done' | 'failed' | null
}

/** True if no source is still pending (including the absent-progress case). */
function arePreviewsSettled(progress: BrandLearningProgress | undefined): boolean {
  if (!progress) return true
  const igSettled = progress.instagram === null || progress.instagram !== 'pending'
  const webSettled = progress.website === null || progress.website !== 'pending'
  return igSettled && webSettled
}

interface ScanningStepProps {
  status: ScanStatus
  /** Display label, e.g. the website hostname or @handle being scanned. */
  target?: string
  /** Raw website URL — drives the "homepage vs profile" copy in the feed. */
  websiteUrl?: string
  /**
   * Per-source state for the brand-learning previews running in parallel
   * with the scan. Drives the deeper-analysis checklist rows.
   */
  brandLearningProgress?: BrandLearningProgress
}

/**
 * Phase 2. The "we're working" moment, made unboring.
 *
 * The DiscoveryFeed card is the focal point: each row reveals a piece of
 * discovered evidence the moment its data lands — Taka narrating "look,
 * here's the logo… here's the name… here are the colors…" The revealed
 * elements (logo, name's layoutId on the headline, color swatches, tone
 * chips) carry shared `layoutId`s and fly to their final slots on the
 * brand card during the phase handoff.
 *
 * Rows are NOT tied to a stage timeline — with the multimodal pipeline,
 * profile data arrives as a streaming burst once the screenshot lands.
 * Each row's pending/done state is driven by data availability alone.
 */
export const ScanningStep: React.FC<ScanningStepProps> = ({
  status,
  target,
  websiteUrl,
  brandLearningProgress,
}) => {
  // The logo only appears once a real one resolves: the server's `logo` event
  // (scraped from the page's static HTML — JSON-LD, apple-touch-icon, header
  // <img>, og:image, rel="icon") populates status.logoUrl, and profile-final
  // confirms the canonical URL (scraped ?? social). We deliberately do NOT use
  // a favicon-service placeholder: providers return a generic glyph (e.g. a
  // grey chevron) for domains they don't recognise, served as a valid image so
  // <img onError> never fires — which renders a fake "logo". Until a real logo
  // lands the row stays hidden; if none ever resolves, BrandCardStep's LogoSlot
  // falls back to the business-name initial.
  const result = status.profile
  const businessName = result?.businessName?.trim()
  const logoSrc = status.logoUrl || result?.logoUrl || null

  const sections = (status.profile?.sections ?? []) as PolaroidSection[]

  // During the FINAL_HOLD_MS window before unmount, fade non-shared chrome so
  // the user doesn't see a ghost layer behind the mounting BrandCardStep.
  // A single exit-faded wrapper would cascade-kill the shared-layoutId morphs
  // (logo / name / colors / tones), so each chrome element fades on its own.
  //
  // Gated on previewsSettled too — without that, the chrome fades the moment
  // the scan stream ends, even though we keep the user on ScanningStep until
  // brand-learning previews finish. Without the gate, the polaroids/feed
  // chrome wink out and the layoutId children hang in mid-air for however
  // long the slowest preview takes.
  const previewsSettled = arePreviewsSettled(brandLearningProgress)
  const isExiting = status.phase === 'complete' && previewsSettled
  const exitFade = {
    animate: { opacity: isExiting ? 0 : 1 },
    transition: { duration: 0.45, ease: [0.4, 0, 0.6, 1] as const },
  }

  return (
    <div className="mx-auto flex max-w-[820px] flex-col gap-7">
      <div className="flex flex-col gap-3">
        <motion.span {...exitFade} className="pill self-start">
          <span className="liveDot" />
          {target ? `Scanning ${target}` : 'Scanning'}
        </motion.span>
        <h2 className="text-[36px] leading-[1.05] font-semibold tracking-[-0.02em] text-paper sm:text-[44px]">
          {businessName ? (
            <>
              <motion.span {...exitFade}>Studying </motion.span>
              <motion.span layoutId={SHARED_LAYOUT_NAME} className="serif-it">
                {businessName}
              </motion.span>
              <motion.span {...exitFade}>.</motion.span>
            </>
          ) : (
            <motion.span {...exitFade}>Studying your business.</motion.span>
          )}
        </h2>
        <motion.p {...exitFade} className="lede max-w-[480px] !text-[16px] !leading-[1.55]">
          Every page, every pixel, every word. The magic's in the details.
        </motion.p>
      </div>

      <div className="flex flex-col gap-7 sm:flex-row sm:items-stretch sm:gap-10">
        <div className="flex-1">
          <DiscoveryFeed
            status={status}
            logoSrc={logoSrc}
            isExiting={isExiting}
            previewsSettled={previewsSettled}
            hasWebsite={!!websiteUrl}
            brandLearningProgress={brandLearningProgress}
          />
        </div>
        <motion.div
          animate={isExiting ? { opacity: 0, x: 32, scale: 0.92 } : { opacity: 1, x: 0, scale: 1 }}
          transition={{ duration: 0.7, ease: [0.4, 0, 0.6, 1] }}
          className="sm:flex sm:items-stretch sm:gap-3"
        >
          <PolaroidStack
            screenshot={status.screenshot ?? null}
            sections={sections}
            instagramProfile={status.instagramProfile ?? null}
            hasWebsite={!!websiteUrl}
          />
        </motion.div>
      </div>
    </div>
  )
}

interface PolaroidStackProps {
  screenshot: ScanStatus['screenshot'] | null
  sections: PolaroidSection[]
  instagramProfile: ScanStatus['instagramProfile'] | null
  /** True when the user gave us a website URL. Drives the website polaroid's
   *  presence — without it, the slot would render as a dead empty card. */
  hasWebsite: boolean
}

/**
 * Stable two-slot layout that renders the website polaroid (when the user
 * gave us a URL) and the IG polaroid (when present). Critically, the JSX
 * *shape* is identical across all states — only classNames flip and slots
 * mount/unmount as their data lands. That preserves the website Polaroid's
 * component identity across the screenshot→both transition, so its `natural`
 * dimensions and auto-scroll progress survive the moment the IG event
 * arrives mid-scan.
 *
 * Two responsive variants render in parallel (mobile / desktop) toggled with
 * `sm:hidden` / `hidden sm:flex`. They're separate trees so framer/React don't
 * try to reconcile a tilted-overlap layout into a snap carousel as the viewport
 * crosses the breakpoint.
 *
 * Desktop: `flex items-stretch` keeps both polaroids the same height — the
 * website polaroid's image scroller relies on `h-full`, so without stretch it
 * collapses when the parent has no intrinsic height.
 *
 * Mobile: a horizontal scroll-snap carousel where each slot is the polaroid's
 * exact width (220px). The carousel's `px-[calc(50vw-110px)]` is the trick that
 * makes both ends snap-center cleanly: with that padding, slot 1 lands centered
 * in the viewport and slot 2 (when present) peeks ~70px from the right edge —
 * the visible affordance that says "swipe to see the rest." `-mx-5` cancels the
 * parent's mobile padding so the carousel and its peek slice extend edge-to-edge.
 * Each slot has explicit `h-[320px]` because the polaroid's internal `h-full`
 * needs a height from its parent. With only one polaroid, the single slot
 * still centers — no broken layout.
 */
const PolaroidStack: React.FC<PolaroidStackProps> = ({
  screenshot,
  sections,
  instagramProfile,
  hasWebsite,
}) => {
  const showInstagram = !!instagramProfile
  // Only tilt/overlap when BOTH cards are present — a lone polaroid stays
  // upright and centered so it doesn't read as a broken layout.
  const isPaired = hasWebsite && showInstagram

  return (
    <>
      <div className="no-scrollbar -mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-[calc(50vw-110px)] sm:hidden">
        {hasWebsite && (
          <div className="h-[320px] w-[220px] shrink-0 snap-center">
            <Polaroid screenshot={screenshot ?? null} sections={sections} />
          </div>
        )}
        {showInstagram && (
          <div className="h-[320px] w-[220px] shrink-0 snap-center">
            <InstagramPolaroid profile={instagramProfile} />
          </div>
        )}
      </div>
      <div className="relative hidden items-stretch sm:flex">
        {hasWebsite && (
          <div className={cn(isPaired && '-mr-3 origin-bottom-right -rotate-[3deg]')}>
            <Polaroid screenshot={screenshot ?? null} sections={sections} />
          </div>
        )}
        {showInstagram && (
          <div className={cn(isPaired && 'origin-bottom-left rotate-[3deg]')}>
            <InstagramPolaroid profile={instagramProfile} />
          </div>
        )}
      </div>
    </>
  )
}

interface DiscoveryFeedProps {
  status: ScanStatus
  logoSrc: string | null
  /** When true, fade row chrome (markers + labels). Reveal data stays visible because it carries shared layoutIds that morph to BrandCardStep. */
  isExiting: boolean
  /**
   * True when every attempted brand-learning preview has settled. Combined
   * with scan phase to compute `isWorking` — without this, a row stops
   * looking active the instant the website scan ends, even if IG/website
   * previews are still mining.
   */
  previewsSettled: boolean
  /** True when the user gave us a website URL — swaps "Reading your homepage" copy for "Reading your profile" when we only have a social handle. */
  hasWebsite: boolean
  brandLearningProgress?: BrandLearningProgress
}

/**
 * `useStaggeredReveals` spaces simultaneous reveals (the common case once the
 * LLM emits partials in a burst) so rows appear one by one instead of dumping
 * in the same tick.
 *
 * Scan rows carry render output (logo / name / swatches / chips) — those
 * children fly to the brand card via shared layoutIds. The brand-learning
 * rows (IG study, website voice) appear last in the checklist and have no
 * morph payload; they're pure progress markers for the deeper analysis
 * running alongside the scan.
 */
const DiscoveryFeed: React.FC<DiscoveryFeedProps> = ({
  status,
  logoSrc,
  isExiting,
  previewsSettled,
  hasWebsite,
  brandLearningProgress,
}) => {
  const result = status.profile
  const exitFade = {
    animate: { opacity: isExiting ? 0 : 1 },
    transition: { duration: 0.45, ease: [0.4, 0, 0.6, 1] as const },
  }

  // Scan rows can be "skipped past" once the scan has finished — at that
  // point any scan row whose data never arrived (e.g. site exposed no
  // colors) will never become eligible, and blocking on it would also stall
  // every subsequent row. Brand-learning rows are never skipped: their
  // 'done'/'failed' resolution always lands eventually.
  const scanComplete = status.phase === 'complete'

  const rows = useMemo<RowDef[]>(() => {
    const list: RowDef[] = SCAN_ROWS.map(r => ({
      id: r.id,
      // "Reading your homepage" makes no sense when the user only handed us a
      // social handle — swap it for "Reading your profile" in that case.
      label: !hasWebsite && r.id === 'name' ? 'Reading your profile' : r.label,
      eligible: r.eligible(result, logoSrc),
      canSkip: scanComplete,
      render: r.render ? () => r.render!(result, logoSrc) : undefined,
    }))
    if (brandLearningProgress?.instagram) {
      list.push({
        id: 'ig-study',
        label: 'Studying your Instagram posts',
        // 'failed' counts as "we're past it" — the row stops looking active.
        eligible:
          brandLearningProgress.instagram === 'done' ||
          brandLearningProgress.instagram === 'failed',
        canSkip: false,
      })
    }
    if (brandLearningProgress?.website) {
      list.push({
        id: 'web-study',
        label: 'Reading your website voice',
        eligible:
          brandLearningProgress.website === 'done' || brandLearningProgress.website === 'failed',
        canSkip: false,
      })
    }
    return list
  }, [result, logoSrc, hasWebsite, brandLearningProgress, scanComplete])

  const revealed = useOrderedStaggeredReveals(rows)
  // The active row is the "currently studying…" focal point. It moves through
  // the checklist in two passes:
  //   1. While the website scan is in flight, focus stays on the first
  //      not-yet-revealed scan row.
  //   2. Once the scan ends (whether every scan row revealed or not), focus
  //      jumps to the first not-yet-revealed brand-learning row.
  // Picking by row category (instead of "first not-revealed across the
  // whole list") avoids a stalled scan row — say, a site that returned no
  // colors — pinning the active focus forever and starving the brand-
  // learning rows of their indicator.
  const scanInFlight = status.phase === 'visiting' || status.phase === 'studying'
  const activeRowId = scanInFlight
    ? rows.find(r => SCAN_ROW_IDS.has(r.id) && !revealed[r.id])?.id
    : !previewsSettled
      ? rows.find(r => BRAND_LEARNING_ROW_IDS.has(r.id) && !revealed[r.id])?.id
      : undefined

  // The outer card and the row's `render(...)` output (which carries the
  // shared layoutId children) are NEVER nested inside a fading motion element
  // — CSS opacity cascades, and a faded ancestor makes the layoutId morph
  // play invisibly. The card backdrop fades via an absolute-positioned layer
  // underneath the row content; per-row chrome (StageIcon + label) fades on
  // its own motion.span. Layout children (logo / colors / chips) stay at
  // full opacity so the eye can track them traveling to the brand card.
  return (
    <div className="relative">
      <motion.div {...exitFade} className="card absolute inset-0" aria-hidden="true" />
      <ul className="relative flex flex-col gap-3.5 p-5 sm:p-6">
        {rows.map(row => {
          const done = revealed[row.id]
          const active = !done && activeRowId === row.id
          return (
            <li key={row.id} className="flex min-h-[28px] items-center justify-between gap-4">
              <motion.div {...exitFade} className="flex min-w-0 items-center gap-3">
                <StageIcon state={done ? 'done' : active ? 'active' : 'pending'} />
                <span
                  className={cn(
                    'text-[14px] transition-colors',
                    active && 'font-medium text-paper',
                    done && 'text-paper-dim',
                    !done && !active && 'text-paper-faint'
                  )}
                >
                  {row.label}
                  {active && <DotsAnimation />}
                </span>
              </motion.div>
              {done && row.render?.()}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/** Row as rendered by DiscoveryFeed — eligibility resolved, render closed over scan state. */
interface RowDef {
  id: string
  label: string
  eligible: boolean
  /** True when the row can be passed over without revealing — e.g. a scan
   *  row whose data never arrived and the scan is now complete. Lets the
   *  ordered reveal walker move on instead of stalling forever. */
  canSkip: boolean
  render?: () => React.ReactNode
}

/** Used by DiscoveryFeed to partition rows into scan vs brand-learning when picking the active focus. */
const BRAND_LEARNING_ROW_IDS: ReadonlySet<string> = new Set(['ig-study', 'web-study'])
const SCAN_ROW_IDS: ReadonlySet<string> = new Set(['logo', 'name', 'colors', 'voice', 'industry'])

const REVEAL_STAGGER_MS = 280

/**
 * Walks a row list in order and reveals each row with a stagger, but only
 * after every row ahead of it is either revealed or explicitly skippable.
 * Without the ordering, fast-completing rows late in the list (e.g. the
 * brand-learning previews) pop their checkmark in before the scan rows
 * above them finish — readers see items 6/7 marked done while 3/4/5 are
 * still spinning, which reads as broken.
 *
 * `canSkip` is the escape hatch: a scan row whose data never arrived
 * after the scan completes is marked skippable, so its absence doesn't
 * stall every row below it forever. Brand-learning rows always resolve
 * eventually and stay canSkip=false.
 *
 * Caller is expected to memoize `rows` so this effect only fires on real
 * changes.
 */
function useOrderedStaggeredReveals<R extends { id: string; eligible: boolean; canSkip: boolean }>(
  rows: ReadonlyArray<R>
): Record<string, boolean> {
  const [revealed, setRevealed] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(rows.map(r => [r.id, false]))
  )
  const revealedRef = useRef(revealed)
  revealedRef.current = revealed

  useEffect(() => {
    const toReveal: string[] = []
    for (const row of rows) {
      if (revealedRef.current[row.id]) continue
      if (row.eligible) {
        toReveal.push(row.id)
      } else if (row.canSkip) {
        continue
      } else {
        // Blocked on this row — every row after it must wait its turn.
        break
      }
    }

    if (toReveal.length === 0) return

    const timeouts: ReturnType<typeof setTimeout>[] = []
    toReveal.forEach((id, i) => {
      timeouts.push(
        setTimeout(() => {
          setRevealed(curr => (curr[id] ? curr : { ...curr, [id]: true }))
        }, i * REVEAL_STAGGER_MS)
      )
    })
    return () => timeouts.forEach(clearTimeout)
  }, [rows])

  return revealed
}

type ScanRowId = 'logo' | 'name' | 'colors' | 'voice' | 'industry'
type Result = ScanStatus['profile']

interface ScanRow {
  id: ScanRowId
  label: string
  /** Whether this row's data has landed and the row should tick to "done". */
  eligible: (result: Result, logoSrc: string | null) => boolean
  /** Optional revealed content — typically carries a shared layoutId that morphs to the brand card. */
  render?: (result: Result, logoSrc: string | null) => React.ReactNode
}

/**
 * The scan-derived discovery rows, top-to-bottom. Reordering them just reorders
 * the UI — eligibility predicates and renderers are co-located here. The
 * brand-learning rows ("Studying your Instagram posts" / "Reading your website
 * voice") are appended dynamically in DiscoveryFeed based on which sources the
 * parent attempted.
 */
const SCAN_ROWS: ScanRow[] = [
  {
    id: 'logo',
    label: 'Saying hi',
    eligible: (_r, logoSrc) => !!logoSrc,
    render: (result, logoSrc) =>
      logoSrc ? <DiscoveryLogoImg src={logoSrc} backdrop={result?.logoBackdrop} /> : null,
  },
  {
    id: 'name',
    label: 'Reading your homepage',
    eligible: result => !!result?.businessName?.trim(),
    render: result => {
      const name = result?.businessName?.trim()
      if (!name) return null
      return (
        <motion.span
          {...REVEAL_ENTER}
          className="max-w-[260px] truncate text-[14px] font-semibold text-paper"
        >
          {name}
        </motion.span>
      )
    },
  },
  {
    id: 'colors',
    label: 'Capturing your palette',
    eligible: result => !!result?.colors,
    render: result => {
      const colors = result?.colors
      if (!colors) return null
      return (
        <motion.div {...REVEAL_ENTER} className="flex items-center gap-1.5">
          {(['primary', 'secondary', 'accent'] as const).map(k => (
            <motion.span
              key={k}
              layoutId={SHARED_LAYOUT_COLOR(k)}
              className="inline-block h-5 w-5 rounded-[5px] border border-line-bright"
              style={{ background: colors[k] }}
              title={colors[k]}
            />
          ))}
        </motion.div>
      )
    },
  },
  {
    id: 'voice',
    label: 'Listening for your voice',
    eligible: result => !!(result?.suggestedTones && result.suggestedTones.length > 0),
    render: result => {
      const tones = result?.suggestedTones?.slice(0, 3) ?? []
      if (!tones.length) return null
      return (
        <motion.div {...REVEAL_ENTER} className="flex flex-wrap items-center justify-end gap-1.5">
          {tones.map(tone => (
            <motion.span
              key={tone}
              layoutId={SHARED_LAYOUT_TONE(tone)}
              className="chip chip-sm"
              data-active="true"
            >
              {tone}
            </motion.span>
          ))}
        </motion.div>
      )
    },
  },
  {
    id: 'industry',
    label: 'Stitching it together',
    eligible: result => !!result?.industry?.trim(),
    render: result => {
      const industry = result?.industry?.trim()
      if (!industry) return null
      return (
        <motion.span
          {...REVEAL_ENTER}
          className="max-w-[280px] truncate text-[13px] text-paper-dim italic"
        >
          {industry}
        </motion.span>
      )
    },
  },
]

// `StageIcon` + `StageState` were promoted to `components/StageIcon` once
// the cockpit's drafting feed needed the same recipe (see
// playbook/design-system/surfaces/product-perfection.md, H-P0-5). Re-exported
// here for consumers that still import from this file.
export { StageIcon, type StageState }

const DotsAnimation: React.FC = () => (
  <span className="ml-1 inline-flex text-paper-dim">
    <span className="animate-pulse">.</span>
    <span className="animate-pulse [animation-delay:200ms]">.</span>
    <span className="animate-pulse [animation-delay:400ms]">.</span>
  </span>
)
